import { useState } from 'react'
import { Link } from 'react-router'
import { useLinkNote, useNoteTasks, useUnlinkNote } from '../../hooks/useTaskNotes.ts'
import { useTasks } from '../../hooks/useTasks.ts'
import { Picker } from '../../ui/Picker.tsx'
import { ErrorState } from '../../ui/primitives.tsx'
import styles from './notes.module.css'

/**
 * The tasks this note is attached to (ADR 0012).
 *
 * The mirror image of the section in the task drawer, and on purpose: a link visible from one side
 * only reads, from the other side, as no link at all. Both ends write through the same endpoint
 * under the task, so there is one rule about what a link is, not two.
 */
export function NoteTasks({ noteId }: { noteId: number }): React.JSX.Element {
  const linked = useNoteTasks(noteId)
  const unlink = useUnlinkNote()
  const [picking, setPicking] = useState(false)

  const tasks = linked.data ?? []

  if (linked.isError) {
    return (
      <div className={styles.taskLinks}>
        <ErrorState
          text="Nie udało się wczytać powiązanych zadań."
          onRetry={() => void linked.refetch()}
        />
      </div>
    )
  }

  return (
    <div className={styles.taskLinks}>
      <div className={styles.taskChips}>
        {tasks.map((task) => (
          <span key={task.id} className={styles.taskChip}>
            <Link className={styles.taskChipLink} to={`/table?task=${task.id}`} title={task.title}>
              <span className={styles.taskChipRef}>{task.ref}</span>
              <span className={styles.taskChipTitle}>{task.title}</span>
            </Link>
            {/* Zarchiwizowane zadanie zostaje na liście — notatka, która karmiła skończoną robotę,
                nadal jej dotyczy. Chip mówi tylko, że tamto już nie jest w grze. */}
            {task.archivedAt && <span className={styles.taskChipArchived}>archiwum</span>}
            <button
              type="button"
              className={styles.taskChipRemove}
              aria-label={`Odepnij notatkę od ${task.ref}`}
              title={`Odepnij notatkę od ${task.ref}`}
              onClick={() => unlink.mutate({ taskId: task.id, noteId })}
            >
              ✕
            </button>
          </span>
        ))}

        {!picking && (
          <button type="button" className={styles.taskChipAdd} onClick={() => setPicking(true)}>
            {tasks.length === 0 ? '+ Podepnij pod zadanie' : '+'}
          </button>
        )}
      </div>

      {picking && (
        <TaskPicker
          noteId={noteId}
          alreadyLinked={tasks.map((t) => t.id)}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  )
}

function TaskPicker({
  noteId,
  alreadyLinked,
  onClose,
}: {
  noteId: number
  alreadyLinked: number[]
  onClose: () => void
}): React.JSX.Element {
  // Bez archiwum: notatkę podpina się do pracy, która jeszcze się dzieje. Powiązanie zrobione
  // wcześniej zostaje widoczne po archiwizacji — to dwie różne sprawy.
  const tasks = useTasks({ scope: 'active' })
  const link = useLinkNote()

  const candidates = (tasks.data ?? []).filter((t) => !alreadyLinked.includes(t.id))

  return (
    <Picker
      label="Szukaj zadania do podpięcia"
      placeholder="Szukaj zadania po tytule albo T-…"
      options={candidates}
      match={(task, needle) =>
        task.title.toLowerCase().includes(needle) || task.ref.toLowerCase().includes(needle)
      }
      renderOption={(task) => (
        <>
          <span className={styles.pickerTaskRef}>{task.ref}</span>
          <span className={styles.pickerTaskTitle}>{task.title}</span>
        </>
      )}
      emptyText={(hasQuery) =>
        tasks.isPending
          ? 'Wczytuję zadania…'
          : hasQuery
            ? 'Żadne zadanie nie pasuje.'
            : 'Wszystkie zadania są już podpięte.'
      }
      onPick={(task) => {
        link.mutate({ taskId: task.id, noteId })
        onClose()
      }}
      onClose={onClose}
    />
  )
}
